/**
 * Frontend view types + PURE display helpers for the manager tracking view
 * (MANAGER-TRACKING-VIEW-V1, V1-P4). Mirrors the backend
 * api/routes/manager-tracking.ts ManagerTrackingResult shape. Side-effect-free
 * (no React, no CSS) so the render tests can import the helpers directly and
 * pin the single-computation guarantee (tile counts == matrix column sums ==
 * health) without a fetch.
 *
 * SEE-NOT-DO: these types + helpers describe a READ-ONLY aggregate -- counts,
 * staff/clinic labels, urgency levels. No operator action, no lead-level PHI.
 * The six item-9 categories map to the EXISTING design-language status token
 * families (never a new one-off colour): the tenant's RED/ORANGE/YELLOW/BLUE/
 * GREEN/BLACK are CATEGORY identities carried by icon + label + number, never
 * colour alone (design-language 02 sec 2/3, 03 P1/P4).
 */

// --- Wire shapes (mirror the backend result) --------------------------------

export type ManagerUrgencyLevel = 'calm' | 'warm' | 'breach';

export type ManagerCategoryKey =
  | 'untouched'
  | 'followups_due'
  | 'missed_appointments'
  | 'pending_payments'
  | 'todays_appointments'
  | 'no_activity';

/** STITCH-2 SCOPE 6: one PHI-safe drill row behind a tile (wire mirror of
 *  the backend ManagerTileItem -- same rows as the tile count). */
export interface ManagerTileItemView {
  readonly leadId: number;
  readonly safeLeadId: string;
  readonly displayName: string;
  readonly clinicId: number;
  readonly ageMinutes: number | null;
  readonly stage: string | null;
  readonly ownerLabel: string | null;
  readonly assigned: boolean | null;
}

export interface ManagerCategoryTileView {
  readonly key: ManagerCategoryKey;
  readonly count: number;
  readonly available: boolean;
  readonly comingSource: string | null;
  readonly urgency: ManagerUrgencyLevel;
  readonly oldestAgeMinutes: number | null;
  /** In-place drill rows (may be absent on an older backend mid-rollout). */
  readonly items?: readonly ManagerTileItemView[];
  /** MONEY-COMPLETION-V1 (V3-P3): the pending_payments tile's money facts
   *  (INTEGER MINOR UNITS + disclosures); present only on that tile when
   *  its billing-queue read succeeded. */
  readonly pendingPaymentsFacts?: {
    readonly outstandingTotalMinorUnits: number;
    readonly notConfiguredRowCount: number;
    readonly capped: boolean;
  };
}

export interface ManagerClinicRowView {
  readonly clinicId: number;
  readonly clinicLabel: string;
  readonly depth: number;
  readonly untouched: number | null;
  readonly oldestAgeMinutes: number | null;
  readonly breach: number;
  readonly urgency: ManagerUrgencyLevel;
}

export interface ManagerStaffLoadView {
  readonly ownerLabel: string;
  readonly isPool: boolean;
  readonly load: number;
}

export interface ManagerHealthView {
  readonly open: number;
  readonly unassigned: number;
  readonly overdue: number;
  readonly oldestAgeMinutes: number | null;
  readonly ownershipAvailable: boolean;
}

export interface ManagerThresholdsView {
  readonly firstTouchMinutes: number;
  readonly escalationMinutes: number;
  readonly staleMinutes: number;
}

export interface ManagerTrackingView {
  readonly categories: ManagerCategoryTileView[];
  readonly matrix: {
    readonly clinics: ManagerClinicRowView[];
    readonly staff: ManagerStaffLoadView[];
    readonly worstClinicId: number | null;
    readonly ownershipAvailable: boolean;
  };
  readonly totalBreaches: number;
  readonly health: ManagerHealthView;
  readonly thresholds: ManagerThresholdsView;
}

// --- Category presentation (label + token family; icon lives in the TSX) -----

/**
 * The tenant's item-9 category colour intent, mapped to a design-language
 * status token FAMILY (02 sec 2). The honest-empty categories keep their
 * intent for when their data source lands, but the tile renders a neutral
 * coming-state while `available` is false.
 */
export type ManagerToneKey =
  | 'danger' // RED   untouched
  | 'warning' // ORANGE follow-ups due
  | 'pending' // YELLOW missed appointments (honest-empty today)
  | 'info' // BLUE  pending payments (honest-empty today)
  | 'positive' // GREEN today's appointments
  | 'ink'; // BLACK no-activity

export interface ManagerCategoryMeta {
  readonly label: string;
  readonly tone: ManagerToneKey;
}

const CATEGORY_META: Record<ManagerCategoryKey, ManagerCategoryMeta> = {
  untouched: { label: 'Untouched leads', tone: 'danger' },
  followups_due: { label: 'Follow-ups due today', tone: 'warning' },
  missed_appointments: { label: 'Missed appointments', tone: 'pending' },
  pending_payments: { label: 'Pending payments', tone: 'info' },
  todays_appointments: { label: "Today's appointments", tone: 'positive' },
  no_activity: { label: 'No-activity leads', tone: 'ink' },
};

export function categoryMeta(key: ManagerCategoryKey): ManagerCategoryMeta {
  return CATEGORY_META[key];
}

/**
 * Rider (b) STITCH-2: one-line SCOPE NOTE per tile, derived from the ACTUAL
 * backing predicate in lib/queue-health.ts computeManagerTracking (cited per
 * line) -- never a guess. Rendered as the tile tooltip so a manager knows
 * exactly which number is which; the Untouched note names how it differs
 * from the SLA queue's aged "waiting too long" class.
 */
export function categoryScopeNote(key: ManagerCategoryKey): string {
  switch (key) {
    case 'untouched':
      // queue-health.ts untouchedCount: every unassigned OPEN lead, no age gate.
      return 'Every open lead with no owner, regardless of age. The SLA queue\'s "New leads waiting too long" counts only those past your threshold.';
    case 'followups_due':
      // queue-health.ts: open leads older than the first-touch window.
      return 'Open leads older than your first-touch window.';
    case 'missed_appointments':
      // MISSED-APPOINTMENT-RECOVERY-V1: un-stubbed -- the no_show recovery read
      // (lib/missed-appointment-recovery.ts): appointments a patient missed, in
      // your clinic scope, newest first. The recovery worklist (who / when /
      // clinic / next step) lives on the COC no-show recovery desk.
      return 'Appointments a patient missed (no-show) across your clinics. The recovery worklist - who, when, and the next step - lives on the COC no-show recovery desk.';
    case 'pending_payments':
      // MONEY-COMPLETION-V1 (V3-P3): un-stubbed -- the billing-queue
      // derivation (lib/billing-queue.ts): closed visits with accepted
      // treatment still outstanding, across days, in your clinic scope.
      return 'Closed visits with accepted treatment not yet fully collected, across days, in your clinics. Voided payments never count; partial payments reduce the outstanding. The row list lives on the billing surface.';
    case 'todays_appointments':
      // queue-health.ts countAppointmentsToday: appointment_time within
      // today, ACTIVE scheduling statuses (booked/confirmed) -- the day's
      // still-active schedule; arrived/terminal rows are not in this count.
      return 'Active (booked/confirmed) appointments scheduled for today across your clinics. For one clinic\'s FULL day - arrivals, visits, terminal rows - open a clinic day board.';
    case 'no_activity':
      // queue-health.ts: open leads whose last activity is older than the stale window.
      return 'Open leads with nothing recorded for longer than your stale window.';
  }
}

// --- Pure formatters --------------------------------------------------------

/**
 * Compact whole-unit age label. Mirrors the command-center P4 age indicator
 * (CallcenterCommandCenter.formatAge) so the manager glance reads the SAME way
 * the operator queue does. Null age -> '-'.
 */
export function formatManagerAge(minutes: number | null): string {
  if (minutes === null) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * The tile's sub-line (the wallboard QUALIFIER, design-language 04 sec 3): the
 * oldest-age urgency line when there is one; an honest positive/clear line
 * otherwise. Empty for an honest-empty tile (which renders its own coming
 * line). Pure + testable.
 */
export function tileSubline(tile: ManagerCategoryTileView): string {
  if (!tile.available) return '';
  if (tile.oldestAgeMinutes !== null) {
    return `oldest ${formatManagerAge(tile.oldestAgeMinutes)}`;
  }
  // MONEY-COMPLETION-V1: the pending-payments tile's money companion --
  // INTEGER minor units formatted by string/integer math only; the
  // not-configured rows are DISCLOSED so the total never reads complete
  // when unvalued obligations exist (the NULL-AMOUNT law).
  if (tile.key === 'pending_payments' && tile.pendingPaymentsFacts) {
    const f = tile.pendingPaymentsFacts;
    const rupees = Math.trunc(f.outstandingTotalMinorUnits / 100);
    const paise = Math.abs(f.outstandingTotalMinorUnits % 100);
    const amount =
      paise === 0
        ? `INR ${rupees.toLocaleString('en-IN')}`
        : `INR ${rupees.toLocaleString('en-IN')}.${String(paise).padStart(2, '0')}`;
    const parts = [`${amount} outstanding`];
    if (f.notConfiguredRowCount > 0) {
      parts.push(`+${f.notConfiguredRowCount} without a configured amount`);
    }
    if (f.capped) parts.push('partial view');
    return parts.join(' - ');
  }
  // A warm/breach tile ALWAYS carries a text companion so the urgency is never
  // encoded by colour alone (02 sec 3 / WCAG 1.4.1), even if it has no age.
  if (tile.urgency === 'breach') return 'needs attention';
  if (tile.urgency === 'warm') return 'due';
  if (tile.key === 'todays_appointments') return 'on the schedule';
  if (tile.count === 0) return 'clear';
  return '';
}

/** The command-center drill target -- the read-only queue screen (J2 drill). */
export const MANAGER_COMMAND_CENTER_HREF = '/dashboard/lead-cockpit';

/**
 * Which categories drill to the command-center queue screen. The honest-empty
 * categories (no queue yet) never link -- they render a coming-state.
 * MONEY-COMPLETION-V1: pending_payments never links here either -- its
 * obligations are EPISODES, not leads; the command center is the wrong
 * concept's evidence (the concept-specific drill law). Its row list lives
 * on the billing surface for the roles that hold it.
 * MISSED-APPOINTMENT-RECOVERY-V1: missed_appointments likewise never links to
 * the intake command center -- its rows are missed APPOINTMENTS, not intake
 * leads; the recovery worklist lives on the COC no-show recovery desk (a
 * clinic-scoped operator surface, not a manager page), so the manager tile
 * carries the honest count only.
 */
export function categoryDrillHref(tile: ManagerCategoryTileView): string | null {
  if (tile.key === 'pending_payments' || tile.key === 'missed_appointments') {
    return null;
  }
  return tile.available ? MANAGER_COMMAND_CENTER_HREF : null;
}

/**
 * Per-staff load bar width as a percentage of the busiest staff member (the
 * "who is overloaded" glance). Clamped 0..100; 0 when there is no load.
 */
export function loadBarPercent(load: number, maxLoad: number): number {
  if (maxLoad <= 0) return 0;
  const pct = (load / maxLoad) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/** The busiest staff load in the cut (bar denominator). 0 on an empty cut. */
export function maxStaffLoad(staff: readonly ManagerStaffLoadView[]): number {
  let max = 0;
  for (const s of staff) if (s.load > max) max = s.load;
  return max;
}
